import { useState } from "react";
import { ArrowRight, BadgeCheck, Loader2, Sparkles, MessageCircle } from "lucide-react";
import { createHandoff } from "@/lib/studentsApi";

const SITE = "https://www.entrancezone.com/";

export function WhatsAppShare({ shareUrl, exam, onShare }) {
  const url = /^https?:\/\//.test(shareUrl || "") ? shareUrl : SITE;
  const what = exam ? `${exam} exam alerts` : "exam alerts";
  const text = `I just subscribed to free ${what} from EntranceZone — official date changes, admit cards and results on WhatsApp, plus free AI rank & college prediction. Get yours here: ${url}`;
  const href = `https://wa.me/?text=${encodeURIComponent(text)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onShare} data-testid="whatsapp-share-btn"
      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-800 transition-[background-color,transform] hover:bg-emerald-100 active:scale-[0.98]">
      <MessageCircle className="h-4 w-4" /> Share with friends on WhatsApp
    </a>
  );
}

export function SubscribeSuccess({ result, inIframe, cta, shareUrl, exam, onShare }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canContinue = !!result.grant;
  const live = result.ai_handoff_available !== false;
  const aiHome = (result.ai_url || "https://ai.entrancezone.com").replace(/\/$/, "") + "/?utm_source=entrancezone.com&utm_medium=subscribe_widget";

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const { redirect_url } = await createHandoff(result.student_id, result.grant);
      if (inIframe) window.open(redirect_url, "_top"); else window.location.href = redirect_url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not open EntranceZone AI. Please try again."); setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="subscribe-success">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-emerald-100 p-1.5 text-emerald-700"><BadgeCheck className="h-5 w-5" /></div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-slate-900 md:text-xl">
            {result.status === "existing" ? `Welcome back, ${result.name.split(" ")[0]}!` : `You're in, ${result.name.split(" ")[0]}!`}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {result.status === "existing" ? "Your exam preferences are updated." : "We'll WhatsApp you the moment an official date, admit card or result changes."}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3.5 py-2.5">
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">EntranceZone Student ID</span>
        <span className="font-mono text-sm font-semibold text-slate-900" data-testid="student-id">{result.student_id}</span>
        {result.ai_connected && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">AI account connected</span>}
      </div>
      {canContinue && (
        <div className="rounded-xl bg-slate-900 p-4 text-white">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-amber-300"><Sparkles className="h-3.5 w-3.5" /> Next step · free</div>
          <p className="mt-1.5 text-sm text-slate-200">Get your rank & college prediction and a personalised study plan on EntranceZone AI.</p>
          {live ? (
            <button onClick={go} disabled={busy} data-testid="continue-ai-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color,transform] hover:bg-amber-300 active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{cta || "Continue to EntranceZone AI"}<ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <a href={aiHome} target="_top" rel="noopener" data-testid="explore-ai-link"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition-[background-color,transform] hover:bg-amber-300 active:scale-[0.98]">
              Explore EntranceZone AI<ArrowRight className="h-4 w-4" />
            </a>
          )}
          {err && <p className="mt-2 text-xs text-red-300" data-testid="handoff-error">{err}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <WhatsAppShare shareUrl={shareUrl} exam={exam} onShare={onShare} />
        <span className="text-[11px] text-slate-400">Know someone preparing for the same exam?</span>
      </div>
    </div>
  );
}
